import { useAppSelector } from "@src/store/hooks";
import { startTransition, useEffect, useState } from "react";
import ObjectModel from "../prototypes/ObjectModel";
import LocalStorageModel from "../prototypes/LocalStorageModel";

const parseEventName = (key: string) => {
  return key === "change" ? "change" : `change:${key}`;
};

const useBaseModelChange = <
  T extends keyof M["attributes"],
  M extends ObjectModel | LocalStorageModel,
  K extends Partial<Pick<M["attributes"], T>>,
  L extends T[] | "change"
>(
  list: L,
  model: M
): [L extends "change" ? M["attributes"] : K, M] => {
  const [listenedData, setListenedData] = useState<K>({} as K);
  const setupInitialized = useAppSelector(
    (state) => state.app.setupInitialized
  );

  useEffect(() => {
    let handlers: { [_ in T]?: () => void } = {};

    let changeHandle: (() => void) | null = null;
    let init = () => {};
    if (setupInitialized) {
      // HYDRATION-FIX: startTransition — évite "Suspense boundary received an
      // update before it finished hydrating" sur les Suspense lazy descendants.
      init = () => {
        if (list === "change") {
          const changeData = model.toJSON();
          if (JSON.stringify(changeData) !== JSON.stringify(listenedData)) {
            startTransition(() => setListenedData(changeData));
          }
        } else {
          const _data: K = {} as K;

          list.forEach((key) => {
            _data[key] = model.get(key as string);
          });

          if (JSON.stringify(_data) !== JSON.stringify(listenedData)) {
            startTransition(() => setListenedData(_data));
          }
        }
      };

      // trigger init
      init();

      if (list === "change") {
        changeHandle = () => {
          const changeData = model.toJSON();
          if (JSON.stringify(changeData) !== JSON.stringify(listenedData)) {
            startTransition(() => setListenedData(changeData));
          }
        };
      } else {
        handlers = list.reduce<{ [_ in T]?: () => void }>((acc, key) => {
          acc[key] = () => {
            startTransition(() =>
              setListenedData((prev) => ({
                ...prev,
                [key]: model.get(key as string),
              }))
            );
          };
          return acc;
        }, {});
      }

      Object.entries(handlers).forEach(([key, handler]) => {
        if (handler) {
          model.on(parseEventName(key), handler as () => void);
        }
      });

      if (changeHandle) {
        model.on("change", changeHandle);
      }
    }
    return () => {
      Object.entries(handlers).forEach(([key, handler]) => {
        if (handler) {
          model.off(parseEventName(key), handler as () => void);
        }
      });

      if (changeHandle) {
        model.off("change", changeHandle);
      }
    };
  }, [setupInitialized, list, model, listenedData]);

  return [listenedData, model];
};

export default useBaseModelChange;
